import React from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Target, Compass, Heart } from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionLabel from "@/components/SectionLabel";
import SafeImage from "@/components/SafeImage";
import { buildWaLink } from "@/lib/whatsapp";

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        testId="about-hero"
        eyebrow="About Biodeal Animal Health"
        title="An East African animal health company committed to the people who feed us."
        lede="Biodeal Animal Health provides veterinary and animal health products to veterinarians, livestock producers, farmers and companion animal caregivers — supported by field representatives and technical and veterinary specialists."
        image="https://images.pexels.com/photos/6235241/pexels-photo-6235241.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=1600"
        primaryCta={{
          label: "Talk to the BAH Team",
          href: buildWaLink("Hello Biodeal Animal Health, I'd like to learn more about your company."),
          external: true,
        }}
        secondaryCta={{ label: "Explore Solutions", to: "/solutions" }}
      />

      {/* Story */}
      <section className="py-20 md:py-28 bg-bah-paper" data-testid="about-story">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <SectionLabel>Who we are</SectionLabel>
            <h2 className="mt-4 font-display text-3xl md:text-5xl text-bah-ink leading-tight">
              Behind every healthy animal is a responsible professional.
            </h2>
            <div className="mt-6 space-y-4 text-bah-ink leading-relaxed md:text-lg">
              <p>
                Biodeal Animal Health is an East African animal health company
                committed to providing ever-better support to customers and
                their businesses. Building on 25 years of experience, we deliver
                quality medicines, complemented by local understanding and
                expertise about animal health, the need for food security and
                a variety of services.
              </p>
              <p>
                Our company name, <strong>Biodeal</strong>, is derived from the
                word <em>Bio</em>, which means "pertaining to life" and is
                found in words like natural science, life and living
                organisms. It represents our commitment to assisting
                veterinarians and livestock producers in raising and caring
                for the farm and companion animals on which we all rely.
              </p>
              <p>
                Biodeal is a company that creates, manufactures and sells
                veterinary and animal health products. We provide services to
                veterinarians, livestock producers and people who raise and
                care for farm and companion animals across East Africa.
              </p>
              <p>
                Biodeal Animal Health is supported by field representatives
                and technical / veterinary specialists who work alongside
                farmers — every day.
              </p>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden rounded-3xl border border-bah-line">
              <SafeImage
                src="https://images.pexels.com/photos/3152331/pexels-photo-3152331.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=820"
                alt="Kenyan farmer with livestock"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-bah-ink/70 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-bah-yellow">
                  25+ years
                </div>
                <div className="mt-2 font-display text-2xl leading-snug">
                  Building Biodeal alongside the people who feed East Africa.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision / Mission */}
      <section className="py-20 md:py-28 bg-bah-ink text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="md:col-span-1 rounded-2xl bg-white/[0.04] border border-white/10 p-7">
            <div className="h-10 w-10 rounded-lg bg-bah-yellow/20 text-bah-yellow grid place-content-center">
              <Compass className="h-5 w-5" />
            </div>
            <div className="mt-5 text-[11px] uppercase tracking-[0.22em] text-bah-yellow font-bold">
              Vision
            </div>
            <p className="mt-3 font-display text-2xl leading-snug">
              To be the most trusted brand in East Africa, working with
              farmers to support animal care needs.
            </p>
          </div>
          <div className="md:col-span-1 rounded-2xl bg-white/[0.04] border border-white/10 p-7">
            <div className="h-10 w-10 rounded-lg bg-bah-yellow/20 text-bah-yellow grid place-content-center">
              <Target className="h-5 w-5" />
            </div>
            <div className="mt-5 text-[11px] uppercase tracking-[0.22em] text-bah-yellow font-bold">
              Mission
            </div>
            <p className="mt-3 font-display text-2xl leading-snug">
              To be present in every farm through products and services —
              working hand in hand with the farmer in providing the best care
              to animals.
            </p>
          </div>
          <div className="md:col-span-1 rounded-2xl bg-bah-green border border-bah-greenLight p-7">
            <div className="h-10 w-10 rounded-lg bg-white/15 text-white grid place-content-center">
              <Heart className="h-5 w-5" />
            </div>
            <div className="mt-5 text-[11px] uppercase tracking-[0.22em] text-bah-yellow font-bold">
              Our promise
            </div>
            <p className="mt-3 font-display text-2xl leading-snug">
              Responsible Animal Health. Trusted by Professionals. Built for
              Farmers.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-bah-green text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl md:text-5xl leading-[1.05] max-w-3xl mx-auto">
            Let's talk about animal health in your context.
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a
              href={buildWaLink("Hello Biodeal Animal Health, I'd like to talk to the team.")}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp the BAH Team
            </a>
            <Link to="/contact" className="btn-secondary !border-white !text-white hover:!bg-white/10">
              Visit Contact / WhatsApp Support
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
